import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { OctagonAlert } from 'lucide-react';
import { useSystemNotices } from '@/contexts/SystemNoticesContext';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import type { ActiveNoticeResponse } from '@/api/auth/missionImpactAuthAPI.schemas';

/**
 * Individual notice card component
 */
function NoticeCard({ notice }: { notice: ActiveNoticeResponse }) {
  const { acknowledgeNotice } = useSystemNotices();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  const handleAcknowledge = async () => {
    setIsAcknowledging(true);
    try {
      await acknowledgeNotice({ noticeId: notice.id });
      setIsDialogOpen(false);
    } finally {
      setIsAcknowledging(false);
    }
  };

  return (
    <>
      <Card className="flex-shrink-0 w-fit min-w-[350px]">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <OctagonAlert className="h-5 w-5" />
            {notice.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col min-h-[120px]">
          <p className="text-sm flex-1 max-h-32 overflow-y-auto">{notice.summary}</p>

          <div className="flex justify-end pt-3">
            <Button
              onClick={() => { setIsDialogOpen(true); }}
              size="sm"
              variant="outline"
              className="text-xs"
            >
              Acknowledge
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <OctagonAlert className="h-5 w-5" />
              {notice.title}
            </DialogTitle>
            <DialogDescription>{notice.summary}</DialogDescription>
          </DialogHeader>

          <div
            className="text-sm [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-2 [&_h4]:text-base [&_h4]:font-semibold [&_h4]:mb-2 [&_p]:my-2 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:my-2 [&_li]:my-1 [&_strong]:font-bold [&_b]:font-bold [&_em]:italic [&_i]:italic [&_a]:text-blue-600 [&_a]:underline [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(notice.bodyHtml) }}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsDialogOpen(false); }}>
              Cancel
            </Button>
            <Button onClick={() => { void handleAcknowledge(); }} disabled={isAcknowledging}>
              Acknowledge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * SystemNoticesCard component displays unacknowledged informational system notices on the dashboard
 * Urgent notices are handled separately by the OnboardingContext with a blocking dialog
 * Each notice is rendered as its own card
 */
export function SystemNoticesCard() {
  const { infoNotices, isLoading, error } = useSystemNotices();

  // Show error if notices failed to load
  if (error) {
    return (
      <Card className="flex-shrink-0 w-fit min-w-[350px]">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <OctagonAlert className="h-5 w-5" />
            System Notices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <QueryErrorState
            error={error}
            message="Failed to load system notices"
            onRetry={() => { window.location.reload(); }}
          />
        </CardContent>
      </Card>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <Card className="flex-shrink-0 w-fit min-w-[350px]">
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-8 w-24" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Don't render anything if no notices
  if (infoNotices.length === 0) {
    return null;
  }

  // Render each notice as its own card
  return (
    <>
      {infoNotices.map((notice) => (
        <NoticeCard key={notice.id} notice={notice} />
      ))}
    </>
  );
}
